import { useState, useEffect } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import { getSupabaseConfig, supabase } from '@/lib/supabase';
import { debugStore } from '@/lib/debugStore';
import { useAuth } from '@/contexts/AuthContext';
import * as Linking from 'expo-linking';
import { Copy, RefreshCw } from 'lucide-react-native';

const log = createLogger('DEBUG_AUTH');

export default function DebugAuthScreen() {
  const router = useRouter();
  const { user, session } = useAuth();
  const [debugState, setDebugState] = useState(debugStore.getState());
  const missingValue = '—';

  // V1: Lock dev routes in production
  useEffect(() => {
    if (!__DEV__) { router.replace('/'); }
  }, [router]);

  useEffect(() => {
    const unsubscribe = debugStore.subscribe(state => {
      setDebugState(state);
    });
    return unsubscribe;
  }, []);

  if (!__DEV__) return null;

  const config = getSupabaseConfig();
  const resetPasswordUrl = Linking.createURL('reset-password');

  const debugInfo = {
    'Supabase URL Host': config.urlHost,
    'Anon Key (last 6)': config.anonKeyLast6,
    'Current User ID': user?.id || missingValue,
    'Current User Email': user?.email || missingValue,
    'Session Present': session ? 'true' : 'false',
    'Last Deep Link': debugState.lastDeepLink || missingValue,
    'Last Auth Event': debugState.lastAuthEvent || missingValue,
    'Deep Link Timestamp': debugState.timestamp
      ? new Date(debugState.timestamp).toLocaleString()
      : missingValue,
    'Parsed Params': debugState.lastParsedParams
      ? JSON.stringify(debugState.lastParsedParams, null, 2)
      : missingValue,
    'Platform': Platform.OS,
    'Linking.createURL("reset-password")': resetPasswordUrl,
  };

  const handleCopy = async () => {
    const text = Object.entries(debugInfo)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n');

    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        Alert.alert('Copied', 'Debug info copied to clipboard');
      } else {
        log.info('Debug info:', text);
        Alert.alert('Debug Info', 'Debug info logged to console');
      }
    } catch {
      log.info('Debug info:', text);
      Alert.alert('Debug Info', 'Debug info logged to console');
    }
  };

  const handleSignOut = async () => {
    log.info('[DEBUG_AUTH] Sign out button pressed');
    await supabase.auth.signOut();
    Alert.alert('Signed Out', 'You have been signed out');
  };

  const handleForceLocalSignOut = () => {
    if (!__DEV__) {
      Alert.alert('Error', 'This feature is only available in development mode');
      return;
    }

    Alert.alert(
      'Force Local Sign Out',
      'This will clear local auth state and navigate to welcome WITHOUT calling Supabase signOut. Use this if web/network issues prevent normal sign out.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Force Sign Out',
          style: 'destructive',
          onPress: () => {
            log.info('[DEBUG_AUTH] ========== FORCE LOCAL SIGN OUT ==========');
            log.info('[DEBUG_AUTH] Clearing debug store');
            debugStore.clear();
            log.info('[DEBUG_AUTH] Navigating to welcome screen');
            router.replace('/(auth)/welcome');
            log.info('[DEBUG_AUTH] ========== FORCE SIGN OUT COMPLETE ==========');
            Alert.alert('Success', 'Local state cleared and redirected to welcome');
          },
        },
      ]
    );
  };

  const handleRefresh = () => {
    setDebugState(debugStore.getState());
  };

  const handleSimulateRecovery = async () => {
    if (!__DEV__) {
      Alert.alert('Error', 'This feature is only available in development mode');
      return;
    }

    Alert.alert(
      'Simulate Recovery',
      'This will create a test deep link event. Make sure you have a valid session first.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Simulate',
          onPress: async () => {
            const mockUrl = 'duel://reset-password?type=recovery&token_hash=mock_token_12345';
            log.info('[DEBUG] Simulating recovery deep link:', mockUrl);
            debugStore.setDeepLink(mockUrl, {
              queryParams: { type: 'recovery', token_hash: 'mock_token_12345' },
              fragmentParams: {},
              allParams: { type: 'recovery', token_hash: 'mock_token_12345' },
            });
            Alert.alert('Simulated', 'Check console logs for the simulated deep link event');
          },
        },
      ]
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={{ width: 40 }} />
        <Text style={styles.headerTitle}>Debug Auth</Text>
        <TouchableOpacity onPress={handleRefresh} style={styles.refreshButton}>
          <RefreshCw size={24} color="#FFF" />
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.content}>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Supabase Configuration</Text>
          <View style={styles.card}>
            <InfoRow label="URL Host" value={config.urlHost} />
            <InfoRow label="Anon Key (last 6)" value={config.anonKeyLast6} />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Current Session</Text>
          <View style={styles.card}>
            <InfoRow label="User ID" value={user?.id || missingValue} />
            <InfoRow label="Email" value={user?.email || missingValue} />
            <InfoRow label="Session Present" value={session ? 'true' : 'false'} />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Deep Link Info</Text>
          <View style={styles.card}>
            <InfoRow label="Last Deep Link" value={debugState.lastDeepLink || missingValue} multiline />
            <InfoRow label="Last Auth Event" value={debugState.lastAuthEvent || missingValue} />
            <InfoRow
              label="Timestamp"
              value={
                debugState.timestamp ? new Date(debugState.timestamp).toLocaleString() : missingValue
              }
            />
            <InfoRow
              label="Parsed Params"
              value={
                debugState.lastParsedParams
                  ? JSON.stringify(debugState.lastParsedParams, null, 2)
                  : missingValue
              }
              multiline
            />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Platform Info</Text>
          <View style={styles.card}>
            <InfoRow label="Platform" value={Platform.OS} />
            <InfoRow
              label='Linking.createURL("reset-password")'
              value={resetPasswordUrl}
              multiline
            />
          </View>
        </View>

        {__DEV__ && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Auth Email Status (Dev Only)</Text>
            <View style={styles.card}>
              {debugState.lastPasswordResetAttempt ? (
                <>
                  <InfoRow label="Status" value={debugState.lastPasswordResetAttempt.success ? 'Success' : 'Failed'} />
                  <InfoRow label="Email" value={debugState.lastPasswordResetAttempt.email} />
                  <InfoRow label="Redirect URL" value={debugState.lastPasswordResetAttempt.redirectTo} multiline />
                  <InfoRow
                    label="Timestamp"
                    value={new Date(debugState.lastPasswordResetAttempt.timestamp).toLocaleString()}
                  />
                  {!debugState.lastPasswordResetAttempt.success && debugState.lastPasswordResetAttempt.error && (
                    <>
                      <InfoRow label="Error Message" value={debugState.lastPasswordResetAttempt.error.message} multiline />
                      <InfoRow label="Error Status" value={String(debugState.lastPasswordResetAttempt.error.status || missingValue)} />
                      <InfoRow label="Error Code" value={debugState.lastPasswordResetAttempt.error.code || missingValue} />
                      <View style={styles.guidanceBox}>
                        <Text style={styles.guidanceTitle}>Troubleshooting:</Text>
                        <Text style={styles.guidanceText}>
                          If error = &quot;unexpected_failure&quot; or status = 500, check:
                        </Text>
                        <Text style={styles.guidanceText}>• Supabase Dashboard → Auth → Email Templates</Text>
                        <Text style={styles.guidanceText}>• Supabase Dashboard → Auth → Email Provider</Text>
                        <Text style={styles.guidanceText}>• SMTP configuration if using custom provider</Text>
                      </View>
                    </>
                  )}
                </>
              ) : (
                <Text style={styles.infoValue}>No password reset attempts recorded</Text>
              )}
            </View>
          </View>
        )}

        <View style={styles.buttonContainer}>
          <TouchableOpacity style={styles.copyButton} onPress={handleCopy}>
            <Copy size={20} color="#FFF" />
            <Text style={styles.buttonText}>Copy Debug Info</Text>
          </TouchableOpacity>

          {__DEV__ && (
            <TouchableOpacity style={styles.devButton} onPress={handleSimulateRecovery}>
              <Text style={styles.buttonText}>Simulate Recovery Link (Dev Only)</Text>
            </TouchableOpacity>
          )}

          {user && (
            <TouchableOpacity style={styles.signOutButton} onPress={handleSignOut}>
              <Text style={styles.buttonText}>Sign Out</Text>
            </TouchableOpacity>
          )}

          {__DEV__ && (
            <TouchableOpacity style={styles.forceSignOutButton} onPress={handleForceLocalSignOut}>
              <Text style={styles.buttonText}>Force Local Sign Out (Dev Only)</Text>
            </TouchableOpacity>
          )}
        </View>
      </ScrollView>
    </View>
  );
}

function InfoRow({
  label,
  value,
  multiline,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) {
  return (
    <View style={styles.infoRow}>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text style={[styles.infoValue, multiline && styles.infoValueMultiline]} numberOfLines={multiline ? undefined : 1}>
        {value}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 60,
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#FFF',
  },
  refreshButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  content: {
    flex: 1,
    paddingHorizontal: 20,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#FFF',
    marginBottom: 12,
  },
  card: {
    backgroundColor: '#1a1a1a',
    borderWidth: 1,
    borderColor: '#333',
    borderRadius: 2,
    padding: 16,
    gap: 12,
  },
  infoRow: {
    gap: 4,
  },
  infoLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  infoValue: {
    fontSize: 14,
    color: '#FFF',
    fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
  },
  infoValueMultiline: {
    lineHeight: 20,
  },
  buttonContainer: {
    gap: 12,
    paddingBottom: 40,
  },
  copyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#FF4444',
    paddingVertical: 16,
    borderRadius: 2,
  },
  devButton: {
    backgroundColor: '#FF8800',
    paddingVertical: 16,
    borderRadius: 2,
    alignItems: 'center',
  },
  signOutButton: {
    backgroundColor: '#333',
    paddingVertical: 16,
    borderRadius: 2,
    alignItems: 'center',
  },
  buttonText: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '700',
  },
  guidanceBox: {
    backgroundColor: '#0a0a0a',
    borderWidth: 1,
    borderColor: '#FF8800',
    borderRadius: 2,
    padding: 12,
    marginTop: 8,
  },
  guidanceTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#FF8800',
    marginBottom: 8,
  },
  guidanceText: {
    fontSize: 12,
    color: '#CCC',
    lineHeight: 18,
    marginBottom: 4,
  },
  forceSignOutButton: {
    backgroundColor: '#8B0000',
    paddingVertical: 16,
    borderRadius: 2,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#FF0000',
  },
});
